import { RankingService } from './ranking.service'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { first, switchMap } from 'rxjs/operators'
import { ApiService } from './api.service'
import { AuthService } from './auth.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService, replaceStringTags } from './global.service'
import { Base64, use } from './utils'
import { HttpClient } from '@angular/common/http'

@Injectable({ providedIn: 'root' })
export class EmailService {
  static readonly DEFAULT_SUPPORT_MOBILE = '91 616 15 53'
  type: string
  nick: string
  apiUrl: string
  count: number
  program: any
  private fss: FirestoreService2

  constructor(
    public g: GlobalService,
    private http: HttpClient
  ) {
    console.info(`## ${this.constructor.name}`)
    this.apiUrl = this.g.get('apiUrl')
  }

  emailProgram() {
    return {
      name: this.g.program.name,
      url: this.g.program.url,
      sender: this.g.program.sender,
      email: this.g.program.email,
      imgHeader: this.g.program.images.emailHeader,
      imgFooter: this.g.program.images.emailFooter,
      xrate: this.g.program.xrate,
      subject: '',
      templateId: '99',
      sendWelcomeOnImport: this.g.program.emails.sendWelcomeOnImport || false,
      sendEmailOnApplyPoints: this.g.program.emails.sendEmailOnApplyPoints || false,
      mobile: this.g.program.support?.contact || EmailService.DEFAULT_SUPPORT_MOBILE
    }
  }

  sendMany(items: any[], program: any) {
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`messaging/sendMany`, { items: items, program: program }, true)
  }

  sendOne(item: any, program: any): Observable<any> {
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`messaging/sendOne`, { item: item, program: program }, true)
  }

  sendPlain(mail: any, program: any): Observable<any> {
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`open/sendPlain`, { mail, program }, true)
  }

  /**
   * Sends ONE email based on system templates (editor)
   * NOTE: This method does not support sending email with EXTRA params
   * @param user Object of the user who will receive the email
   * @param template Object representing the template
   */
  sendOneTemplate(user: any, template: any, html = null): Observable<any> {
    const program = {
      sender: this.g.program.sender,
      name: this.g.program.name,
      email: this.g.program.email,
      subject: template.subject,
      html: ''
    }
    const plainHtml = html || JSON.parse(Base64.decode(template.editor)).html

    if (!html && template.ranks && template.ranks.length) {
      const rankSrv: RankingService = use<RankingService>(RankingService)
      return rankSrv.generateRankEmailRows(template.ranks, plainHtml)
        .pipe(switchMap(html => this.sendOne(user, { ...program, html: this.replaceEmailTags(user, html) })))
    } else return this.sendOne(user, { ...program, html: this.replaceEmailTags(user, plainHtml) })
  }

  /**
   * Sends Multiple emails based on system templates (editor)
   * NOTE: This method does not support sending email with PARAMS (extra parameters)
   * @param users Array with user objects who will receive the emails
   * @param template Object representing the template
   */
  private sendManyTemplate(users: any[], template: any, auth: any, html = null): Promise<any> {
    return new Promise<any>(async resolve => {
      if (typeof Worker !== 'undefined') {
        auth.getUserIdToken().subscribe(token => {
          const program = {
            ...this.emailProgram(),
            subject: template.subject
          }
          const plainHtml = html || JSON.parse(Base64.decode(template.editor)).html
          const arr: any[] = []

          for (const oneUser of users) {
            arr.push({ item: oneUser, program: { ...program, html: this.replaceEmailTags(oneUser, plainHtml) } })
          }

          if (arr.length > 100) alert('Os emails serão enviados em lotes de 100 em segundo plano.\nAcompanhe o progresso no Console\nPodes continuar a navegar no sistema, mas não faça refresh e não feche o navegador')
          // creates a new web worker
          this.http.get('workers/email.worker.ts', { responseType: 'blob' }).subscribe(blob => {
            const worker = new Worker(URL.createObjectURL(blob))
            worker.onmessage = ({ data }) => {
              if (data.message) console.log('=>', data.message)
              if (data.action === 'send-many' && data.finished) worker.terminate()
            }
            worker.postMessage({
              action: 'send-many',
              items: arr,
              url: `${this.g.newApiUrl}/api/v1/messaging/sendOne`,
              token
            })
          })
          resolve(true)
        })
      } else {
        // Web workers are not supported in this environment.
        // You should add a fallback so that your program still executes correctly.
        console.error('Web workers are not supported in this environment.')
        for (const item of users.map(({ displayName, firstName, balance, email, segment }) => ({ displayName, firstName, balance, email, segment }))) {
          await this.sendOneTemplate(item, template).pipe(first()).toPromise()
        }
        resolve(true)
      }
    })

  }

  sendCampaignTemplate(users: any[], template: any) {
    return new Promise<any>((resolve, reject) => {
      const auth = use<AuthService>(AuthService)
      if (template.ranks && template.ranks.length) {
        const rankSrv: RankingService = use<RankingService>(RankingService)
        rankSrv.generateRankEmailRows(template.ranks, JSON.parse(Base64.decode(template.editor)).html).subscribe(html => {
          this.sendManyTemplate(users, template, auth, html).then(ok => resolve(ok)).catch(er => reject(er))
        })
      } else {
        this.sendManyTemplate(users, template, auth).then(ok => resolve(ok)).catch(er => reject(er))
      }

    })
  }

  sendConfirmationCardLoadEmail(card, user: any = null): Observable<any> {
    const _program = {
      name: this.g.program.name,
      sender: this.g.program.sender,
      email: this.g.program.email,
      url: this.g.program.url,
      subject: `Registo do carregamento do ${card.name} nº ${card.cardId}`,
      html: `O carregamento do ${card.name} nº ${card.cardId} ficou registado na plataforma ${this.g.program.name} e no prazo de 10 dias úteis o carregamento será efectuado.<br><br>
      <b>Muito importante:</b>
      <br>Por favor confirma o nº série do cartão e caso este não coincida com o cartão que tens em teu poder, liga-nos para o  21 406 76 94<br><br>
      Caso tenhas alguma dúvida, contacta o <b>serviço de apoio</b>.<br>
      (Disponível de segunda a sexta das 9h30 às 13:00 e das 14:00 às 18h00)<br>`,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      templateId: this.g.program.emails['generic']
    }

    const _item = {
      displayName: user.displayName,
      email: user.email,
    }
    const api: ApiService = use<ApiService>(ApiService)
    return api.post('messaging/sendOne', { item: _item, program: _program }, true)
  }

  /**
   * Sends an order confirmation email
   * This method can send this email using a Sendingblue template or system templates (editor)
   * @param order
   */
  async sendConfirmationOrderEmail(order: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    // if templateId is not a Number, then it is an internal template ID (editor)
    // if it is a number, it is a Sendingblue template
    if (isNaN(order.program.templateId)) {
      return this.fss.get('templates', order.program.templateId).pipe(first()).toPromise().then(template => {
        const item = {
          name: order.displayName,
          firstName: order.displayName.split(' ')[0],
          email: this.g.targetOrderToBuyer(order).email,
        }
        const params = {
          'id': order.id,
          'program_url': order.program.url,
          'name': order.displayName,
          'address1': order.deliveryTo.address1,
          'address2': order.deliveryTo.address2,
          'postcode': order.deliveryTo.postCode,
          'city': order.deliveryTo.city,
          'order_date': order.orderDate.substring(0, 10),
          'order_qty': order.orderQty,
          'order_amt': order.orderPoints,
          'delivery_date': order.deliveryDate,
          'contactus': `${order.program.url}/rewards/my-account/contactus`,
          'cart': Array.from(order.cart),
        }
        const program = {
          sender: this.g.program.sender,
          name: this.g.program.name,
          email: this.g.program.email,
          subject: order.subject,
          html: this.replaceEmailTags(item, JSON.parse(Base64.decode(template.editor)).html.replace(/\n/g, ''), params)
        }
        return this.sendOne(item, program).toPromise()
      })
    } else {
      if (order.cartType === 'cartEuros') {
        order.orderPoints = Number(order.orderEuros).toFixed(2)
        for (const c of order.cart) {
          c.orderPoints = (c.orderEuros || 0).toFixed(2)
          c.amtPoints = (c.amtEuros || 0).toFixed(2)
          c.points = (c.priceEuros || 0).toFixed(2)
        }

        // Use subsidy values from order object (already set by prepareOrder with user's toggle choice)
        // Only recalculate if values are missing (backward compatibility)
        if (!order.hasOwnProperty('subsidyAmount') || !order.hasOwnProperty('paymentValue')) {
          const cartItems = order.cart || []
          const total = cartItems
            .map(i => (+(i.priceEuros || 0)) * (+(i.qty || 1)))
            .reduce((acc, val) => acc + val, 0)
          const subsidyPercent = this.g.program.maxOrderSubsidy || 0
          const subsidyAmount = total * (subsidyPercent / 100)
          const paymentValue = total - subsidyAmount

          order.totalBeforeDiscount = Math.round(total * 100) / 100
          order.subsidyPercent = subsidyPercent
          order.subsidyAmount = Math.round(subsidyAmount * 100) / 100
          order.paymentValue = Math.round(paymentValue * 100) / 100
        }
      } else {
        if (order.orderPoints !== undefined) order.orderPoints = Number(order.orderPoints)
      }
      // sends with sendingblue template
      const api: ApiService = use<ApiService>(ApiService)
      return api.post(`messaging/sendOrderConfirmation`, this.g.targetOrderToBuyer(order), true).pipe(first()).toPromise()
    }
  }

  normalizeSellerOrderEmail(order: any): any {
    const _order = { ...order }
    const cartItems = _order.cart || []
    _order.program = {
      ...(_order.program || {}),
      templateId: this.g.program.emails['sellerEmail']
    }
    _order.subject = _order.subject || 'Pedido de encomenda '
    _order.orderQty = cartItems.reduce((total: number, item: any) => total + (Number(item.qty) || 1), 0)

    if (_order.cartType === 'cartEuros') {
      const totalEuros = cartItems.reduce((total: number, item: any) => total + (Number(item.priceEuros || 0) * (Number(item.qty) || 1)), 0)
      _order.orderEuros = _order.orderEuros ?? totalEuros
      _order.orderPoints = Number(_order.orderEuros || 0).toFixed(2)
      _order.cart = cartItems.map((c: any) => ({
        ...c,
        orderPoints: Number(c.orderEuros || 0).toFixed(2),
        amtPoints: Number(c.amtEuros || 0).toFixed(2),
        points: Number(c.priceEuros || 0).toFixed(2),
        priceEuros: Number(c.priceEuros || 0).toFixed(2),
        amtEuros: Number(c.amtEuros || 0).toFixed(2)
      }))
    } else {
      const totalPoints = cartItems.reduce((total: number, item: any) => total + (Number(item.points || 0) * (Number(item.qty) || 1)), 0)
      _order.orderPoints = _order.orderPoints ?? totalPoints
      _order.totalBeforeDiscount = _order.orderPoints
      _order.subsidyPercent = 0
      _order.subsidyAmount = 0
      _order.paymentValue = _order.orderPoints
      _order.cart = cartItems.map((c: any) => {
        const itemPoints = Number(c.points || 0)
        const itemAmtPoints = Number(c.amtPoints || (itemPoints * (Number(c.qty) || 1)))
        return {
          ...c,
          points: itemPoints,
          amtPoints: itemAmtPoints,
          priceEuros: itemPoints,
          amtEuros: itemAmtPoints
        }
      })
    }

    return _order
  }

  sendOrderEmailToSeller(order: any): Promise<any> {
    const _order = this.normalizeSellerOrderEmail(order)
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`messaging/sendOrderConfirmation`, _order, true).pipe(first()).toPromise()
  }

  // TODO WG change to system template
  sendPaymentOk(order: any): Observable<any> {
    this.addSubsidyDataToOrder(order)
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`messaging/sendPaymentOk`, this.g.targetOrderToBuyer(order), true)
  }

  // TODO WG change to system template
  sendPaymentPending(order: any): Observable<any> {
    this.addSubsidyDataToOrder(order)
    const api: ApiService = use<ApiService>(ApiService)
    return api.post(`messaging/sendPaymentPending`, this.g.targetOrderToBuyer(order), true)
  }

  private addSubsidyDataToOrder(order: any): void {
    if (order.cartType === 'cartEuros' && order.cart?.length) {
      // Use subsidy values from order object (already set by prepareOrder with user's toggle choice)
      // Only recalculate if values are missing (backward compatibility)
      if (!order.hasOwnProperty('subsidyAmount') || !order.hasOwnProperty('paymentValue')) {
        const cartItems = order.cart
        const total = cartItems
          .map(i => (+(i.priceEuros || 0)) * (+(i.qty || 1)))
          .reduce((acc, val) => acc + val, 0)
        const subsidyPercent = this.g.program.maxOrderSubsidy || 0
        const subsidyAmount = total * (subsidyPercent / 100)
        const paymentValue = total - subsidyAmount

        order.totalBeforeDiscount = Math.round(total * 100) / 100
        order.subsidyPercent = subsidyPercent
        order.subsidyAmount = Math.round(subsidyAmount * 100) / 100
        order.paymentValue = Math.round(paymentValue * 100) / 100
      }
    }
  }

  /**
   * Replaces special TAGS in the template with user data
   * @param user The user to be filled in
   * @param template The template to be replaced
   */
  replaceEmailTags(user: any, template: string, params: any = null): string {
    return replaceStringTags(user, template, this.g, params)
  }

}
